import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faClose, faEnvelope, faPaperPlane, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  PhoneInput,
  Textarea,
} from '@playcastdotio/Shadcn';
import { useContext, useEffect, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import { toast } from 'sonner';
import clsx from 'clsx';
import { generateQRCode } from '@playcastdotio/Qrcode';
import pcLogo from '../../../assets/playcast_logo-96x96.png';
import { Link } from 'react-router-dom';
import constants from '@playcastdotio/GlobalConfig';

type LocalProps = {
  title: string;
  buttonText: string;
  buttonIcon: IconProp;
  placeholder: string;
  onSend: (target: string, message: string) => void;
  type: 'email' | 'sms';
};

const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const isValidEmail = (email: string): boolean => {
  const regexPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regexPattern.test(email);
};
const isValidPhoneNumber = (phone: string): boolean => {
  // Starts with +, followed by numbers, spaces, dashes, or parentheses
  const regexPattern = /^\+[\d\s\-()]+$/;
  return regexPattern.test(phone);
};

const formatPhoneNumber = (phone: string): string => {
  // Remove spaces, dashes, and parentheses
  const cleanedPhone = phone.replace(/[\s\-()]/g, '');
  return cleanedPhone;
};

export const InviteModule = ({ title, buttonText, buttonIcon, placeholder, onSend, type }: LocalProps) => {
  const targetInputErrorMessage = type === 'email' ? 'Invalid email address' : 'Invalid phone number';
  const messageInputErrorMessage = 'Message exceeds 200 characters';

  const context = useContext(PlaycastContext);
  const [disableButton, setDisableButton] = useState(false);
  const defaultMessage = "You're invited to join this Playcast session";
  const [target, setTarget] = useState('');
  const [message, setMessage] = useState(defaultMessage);
  const [validMessage, setValidMessage] = useState(true);
  const [validTarget, setValidTarget] = useState(true);
  const [formError, setFormError] = useState('');
  const [limitExceeded, setLimitExceeded] = useState(false);
  const [authId, setAuthId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [remainingInvites, setRemainingInvites] = useState(0);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  const [qrCodeLink, setQrCodeLink] = useState<string>('');

  useEffect(() => {
    const streamUrl = `${context.stateStores.invitationStateV2.type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${context.stateStores.invitationStateV2.id}`;
    setQrCodeLink(`sms:?body=You're%20invited%20to%20join%20this%20Playcast%20session:%20${streamUrl}`);
  }, [context.stateStores.invitationStateV2.id, context.stateStores.invitationStateV2.type]);

  useEffect(() => {
    const generateQR = async () => {
      await generateQRCode(qrCodeLink, { logo: pcLogo })
        .then((svgString: string | null | undefined) => {
          if (svgString) {
            setQrCodeUrl(svgString);
          }
        })
        .catch((error) => {
          console.error('Failed to generate QR code:', error);
        });
    };

    if (qrCodeLink) {
      generateQR();
    }
  }, [qrCodeLink]);

  // Clean up the blob URL when the component unmounts to avoid memory leaks
  useEffect(() => {
    return () => {
      if (qrCodeUrl) {
        URL.revokeObjectURL(qrCodeUrl);
      }
    };
  }, [qrCodeUrl]);

  useEffect(() => {
    if (context.stateStores.hostState.status === 'streaming') {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [context.stateStores.hostState.status]);

  useEffect(() => {
    if (type === 'email') {
      setLimitExceeded(context.stateStores.invitationSendState.reachedEmailInviteLimit);
      const emailCount = context.stateStores.invitationSendState.emailCount;
      const emailLimit = context.stateStores.invitationSendState.emailLimit;

      setRemainingInvites(emailLimit - emailCount);
    } else if (type === 'sms') {
      setLimitExceeded(context.stateStores.invitationSendState.smsCount >= context.stateStores.invitationSendState.smsLimit);
      const smsCount = context.stateStores.invitationSendState.smsCount;
      const smsLimit = context.stateStores.invitationSendState.smsLimit;

      setRemainingInvites(smsLimit - smsCount);
    }
    if (limitExceeded) {
      setFormError(`You've reached the limit of 10 ${type === 'email' ? 'Email' : 'Sms'} invites for today. Please try again later.`);
    }
  }, [
    context.stateStores.invitationSendState.emailCount,
    context.stateStores.invitationSendState.emailLimit,
    context.stateStores.invitationSendState.reachedEmailInviteLimit,
    context.stateStores.invitationSendState.smsCount,
    context.stateStores.invitationSendState.smsLimit,
    context.stateStores.invitationSendState.reachedSmsInviteLimit,
    type,
    limitExceeded,
  ]);

  useEffect(() => {
    setAuthId(context.stateStores.loginProfileState.hostAuthId);
  }, [context.stateStores.loginProfileState.hostAuthId]);

  const handleTargetChanged = (value: string | React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = '';
    if (typeof value === 'string') {
      inputValue = value;
    } else {
      if (value === null || value === undefined) {
        inputValue = '';
      } else {
        inputValue = value.target.value;
      }
    }
    let valid = false;

    if (type === 'email') {
      valid = isValidEmail(inputValue);
      setTarget(inputValue);
    } else if (type === 'sms') {
      valid = isValidPhoneNumber(inputValue);
      const formattedTarget = formatPhoneNumber(inputValue);
      setTarget(formattedTarget);
    }
    setValidTarget(valid);
    if (valid) {
      if (!validMessage) {
        setFormError(targetInputErrorMessage);
      } else {
        setFormError('');
      }
      return;
    } else {
      if (!validMessage) {
        setFormError(`${targetInputErrorMessage} and ${messageInputErrorMessage}`);
      } else {
        setFormError(targetInputErrorMessage);
      }
    }
  };

  const handleMessageChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const messageLength = e.target.value.length;
    if (messageLength > 200) {
      setValidMessage(false);
      if (!validTarget) {
        setFormError(`${targetInputErrorMessage} and ${messageInputErrorMessage}`);
      } else {
        setFormError(messageInputErrorMessage);
      }
      return;
    }
    setValidMessage(true);
    if (!validTarget) {
      setFormError(targetInputErrorMessage);
    } else {
      setFormError('');
    }
    setMessage(e.target.value);
  };

  useEffect(() => {
    setMessage(defaultMessage);
    setTarget('');
  }, []);

  const handleClicked = () => {
    if (!validMessage || !validTarget) return;
    if (authId && authId.startsWith('pcmid_')) {
      return toast(<div className='text-orange-600'>You must login to send invites via {type}.</div>);
    }
    onSend(target, message);
    setTarget('');
    setMessage('');
    setFormError('');
    setValidMessage(true);
    setValidTarget(true);
    setDialogOpen(false);
  };

  const handleDialogOpen = () => {
    if (disableButton) return;
    setDialogOpen(true);
  };

  return (
    <Dialog open={dialogOpen}>
      <DialogTrigger asChild>
        <button
          className='group flex items-center'
          onMouseDown={(e) => {
            e.stopPropagation();
            handleDialogOpen();
          }}
        >
          <div className='group-hover:border-midnight-bright flex w-full cursor-pointer gap-2 rounded-md border-2 border-gray-300 p-2 text-left text-sm font-normal group-hover:border-dashed'>
            <FontAwesomeIcon icon={buttonIcon} />
            <Label className='cursor-pointer'>{buttonText}</Label>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className='border-none bg-transparent text-center sm:max-w-[425px]' onPointerDownOutside={() => setDialogOpen(false)}>
        <DialogHeader>
          <DialogTitle className="text-primary text-center font-['Syne'] text-2xl">{title}</DialogTitle>
          <DialogDescription className='text-center text-white'></DialogDescription>
        </DialogHeader>
        {(type === 'email' || (type === 'sms' && !limitExceeded)) && (
          <>
            <DialogDescription className='text-center text-white'>
              {type === 'email' ? `Send an email invite (${remainingInvites} left)` : `Send an SMS invite (${remainingInvites} left)`}
            </DialogDescription>
            <div className='flex flex-col gap-y-4 text-white'>
              <div className='relative items-center'>
                {type === 'email' ? (
                  <Input
                    type='text'
                    placeholder={placeholder}
                    value={target}
                    disabled={limitExceeded}
                    className={clsx(validTarget ? 'border-emerald-600' : 'border-red-600', 'pr-10')}
                    onChange={handleTargetChanged}
                  />
                ) : (
                  <PhoneInput
                    className={clsx(validTarget ? 'border-emerald-600' : 'border-red-600', '')}
                    placeholder={placeholder}
                    value={target}
                    disabled={limitExceeded}
                    onChange={handleTargetChanged}
                    defaultCountry='US'
                  />
                )}
                <FontAwesomeIcon
                  icon={type === 'sms' ? faPhone : faEnvelope}
                  className='absolute right-4 top-5 -translate-y-1/2 transform'
                />
              </div>
              <Textarea
                placeholder='Custom message (0/200)'
                value={message}
                disabled={limitExceeded}
                className={validMessage ? 'border-emerald-600' : 'border-red-600'}
                onChange={handleMessageChanged}
              />
            </div>
            <DialogFooter>
              <DialogClose asChild className='w-full'>
                <Button
                  type='button'
                  className='m-0 w-full gap-x-2 bg-gray-600 text-white hover:bg-gray-700 focus:bg-gray-500 active:bg-gray-500'
                  onClick={() => {
                    setTarget('');
                    setMessage('');
                    setDialogOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faClose} />
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type='submit'
                className='m-0 w-full gap-x-2 bg-emerald-600 text-white hover:bg-emerald-700 focus:bg-emerald-500 active:bg-emerald-500'
                disabled={!validMessage || !validTarget || limitExceeded}
                onClick={handleClicked}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Send
              </Button>
            </DialogFooter>
          </>
        )}

        {type === 'sms' && limitExceeded && <QRMessage image={qrCodeUrl} qrCodeLink={qrCodeLink} />}
      </DialogContent>
    </Dialog>
  );
};

const QRMessage = ({ image, qrCodeLink }: { image: string; qrCodeLink: string }) => {
  return (
    <div className='flex flex-col items-center gap-4'>
      <Label className='text-md text-amber-400'>Out of daily app messages</Label>
      <Label className='text-amber-400'>Scan this code to message your friends.</Label>
      <img src={image} alt='QR Code' className='mx-auto h-64 w-64' />
      {isTouchDevice() ? (
        <Link className='w-fit italic text-amber-400 underline' to={qrCodeLink}>
          Or tap this link
        </Link>
      ) : (
        ''
      )}
    </div>
  );
};
